/**
 * **전적검색의 학습 결과** (사용자 2026-10-29 "시뮬레이션 권한이 있는 사람은 전적검색에 몹을 검색하면 학습 결과도 공덱목록에 보이게 해")
 *
 * 고른 방덱의 학습 기록 공덱을 공덱 목록 맨 위에 싣는다. 방덱은 `/search-counter` 와 같은 잣대 — 리더는 자리까지, 2·3번은 순서 없이,
 * 콜라보 짝은 같은 몹. 시뮬 권한(`user.simViewer`)이 있을 때만 묻는다(`/api/sim/memory` 도 그 권한 뒤에 있다).
 * 학습 기록 전체는 18MB 라 그 방덱 것만 받는다(`?slots=`). 줄을 누르면 시뮬 탭이 그 방덱·공덱으로 칸을 채운다(SimPage `loadLearned`).
 */
import React, { useEffect, useState } from 'react';
import MonsterImage from '../../components/MonsterImage';
import { getMonsterImgPath } from '../../utils/monster';
import { openLearnedInSim } from '../../utils/simHandoff';
import { monstersById, MemoryDeck, MemoryRecord } from '../SimMemory';

/** 사이트 몹(이름·속성) → 받는 몹 번호들 (각성 전후 칸 모두 + 콜라보 짝) */
function idsOf(mapping: any, m: { name: string; attribute: string }): number[] {
    const out: number[] = [];
    for (const fam of [m.name, mapping?.monsters?.[m.name]?.collab].filter(Boolean)) {
        const forms = mapping?.monsters?.[fam]?.[m.attribute];
        if (!forms || typeof forms !== 'object' || Array.isArray(forms)) continue;
        for (const f of Object.values<any>(forms)) {
            const raw = f?.unit_master_id;
            for (const v of Array.isArray(raw) ? raw : [raw]) { const id = Number(v); if (id > 0) out.push(id); }
        }
    }
    return out;
}

/** 같은 방덱을 다시 눌러도 다시 묻지 않는다 (페이지를 새로 열면 다시) */
const cache = new Map<string, Promise<MemoryRecord[]>>();
function fetchLearned(slots: string): Promise<MemoryRecord[]> {
    let p = cache.get(slots);
    if (!p) {
        p = fetch(`/api/sim/memory?slots=${encodeURIComponent(slots)}`, { credentials: 'include' })
            .then((r) => r.json()).then((d) => (d?.success ? (d.records || []) : []))
            .catch(() => []);
        p.then((rs) => { if (!rs.length) cache.delete(slots); }); // 없던 것은 학습한 뒤 다시 볼 수 있게
        cache.set(slots, p);
    }
    return p;
}

type Row = { rec: MemoryRecord; deck: MemoryDeck };
/** 처음 보이는 줄 수 — 나머지는 [더 보기] */
const FIRST = 3;

export default function LearnedDecks({ user, team, monsterMapping, isMobile }: {
    user: any;
    /** 고른 방덱 (리더 먼저). 없거나 공덱으로 찾는 중이면 null */
    team: Array<{ name: string; attribute: string }> | null;
    monsterMapping: any;
    isMobile: boolean;
}) {
    const [rows, setRows] = useState<Row[]>([]);
    const [all, setAll] = useState(false);
    const on = !!user?.simViewer && !!team && team.length === 3;
    const slots = on ? team!.map((m) => idsOf(monsterMapping, m).join(',')).join('|') : '';
    useEffect(() => {
        setRows([]);
        setAll(false);
        if (!slots || slots.split('|').some((s) => !s)) return;
        let live = true;
        fetchLearned(slots).then((recs) => {
            if (!live) return;
            // 받은 기록은 모두 이 방덱(2·3번 차례·콜라보 짝만 다른 것 포함)이다 — 가장 최근 학습 하나만 (학습 기록 창 latestPerDefense 와 같다)
            const rec = recs.reduce<MemoryRecord | null>((m, r) => (!m || r.at > m.at ? r : m), null);
            // 같은 몹 조합(룬 방향·리더·룬 맞추기만 다른 덱)은 승률이 가장 높은 하나만 — 학습 기록 창(SimMemory)과 같다
            const best = new Map<string, Row>();
            if (rec) for (const deck of rec.decks || []) {
                const k = [...deck.units].map(Number).sort((x, y) => x - y).join(',');
                const cur = best.get(k);
                if (!cur || deck.win > cur.deck.win) best.set(k, { rec, deck });
            }
            setRows([...best.values()].sort((a, b) => b.deck.win - a.deck.win));
        });
        return () => { live = false; };
    }, [slots]);
    if (!on || !rows.length) return null;

    const byId = monstersById(monsterMapping);
    const size = isMobile ? 30 : 36;
    const face = (id: number, i: number) => {
        const m = byId.get(Number(id));
        const img = m ? getMonsterImgPath(monsterMapping, m) : null;
        return img?.imgPath
            ? <MonsterImage key={i} imgPath={img.imgPath} alt={img.displayName} style={{ width: size, height: size, borderRadius: 8, objectFit: 'contain', background: '#fff', border: '1.5px solid rgba(102, 126, 234, 0.15)' }} />
            : <div key={i} style={{ width: size, height: size, borderRadius: 8, background: '#f5f5f5', color: '#999', fontSize: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{id}</div>;
    };
    const pct = (x: number) => `${(x * 100).toFixed(1)}%`;
    const shown = all ? rows : rows.slice(0, FIRST);
    const at = rows.reduce((m, r) => (r.rec.at > m ? r.rec.at : m), '');
    const d = new Date(at);
    return (
        <div style={{
            marginBottom: 12, borderRadius: 12, padding: isMobile ? '8px 10px' : '10px 14px', textAlign: 'left',
            border: '1.5px solid rgba(118, 75, 162, 0.35)', background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.08) 0%, rgba(118, 75, 162, 0.08) 100%)'
        }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <span style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: '#fff', fontSize: 10, fontWeight: 800, padding: '3px 8px', borderRadius: 6, whiteSpace: 'nowrap' }}>학습</span>
                <span style={{ fontSize: isMobile ? 12 : 13, fontWeight: 800, color: '#5a4fb0' }}>시뮬 학습 결과</span>
                {!Number.isNaN(d.getTime()) && <span style={{ marginLeft: 'auto', fontSize: 11, color: '#999' }}>{`${d.getMonth() + 1}/${d.getDate()} 학습`}</span>}
            </div>
            {shown.map(({ rec, deck }, i) => (
                <div key={i} onClick={() => openLearnedInSim(rec, deck)} title="시뮬레이션 탭에서 이 방덱·공덱으로 칸 채우기" style={{
                    display: 'flex', alignItems: 'center', gap: isMobile ? 6 : 10, padding: '7px 2px', cursor: 'pointer',
                    borderTop: i ? '1px solid rgba(102, 126, 234, 0.12)' : 'none'
                }}>
                    <span style={{ background: i === 0 ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : '#e8ebf5', color: i === 0 ? '#fff' : '#667eea', borderRadius: 999, padding: '1px 7px', fontSize: 11, fontWeight: 900, flexShrink: 0 }}>{i + 1}</span>
                    <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>{deck.units.map(face)}</div>
                    <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                        {!isMobile && deck.first != null && deck.fastWin != null && (
                            <span title="선턴 확률 · 빠른 방덱(공속 상위 25%) 상대 승률" style={{ fontSize: 11, fontWeight: 700, color: '#888', whiteSpace: 'nowrap' }}>
                                선턴 {Math.round(deck.first * 100)}% · 빠른방덱 {Math.round(deck.fastWin * 100)}%
                            </span>
                        )}
                        <span title="시뮬 승률 (어려운 방덱 기준)" style={{ fontSize: 13, fontWeight: 900, color: deck.win >= 0.5 ? '#1976d2' : '#d32f2f', whiteSpace: 'nowrap' }}>{pct(deck.win)}</span>
                        {!isMobile && <span style={{ border: '1.5px solid #667eea', borderRadius: 8, color: '#4b5fd6', background: '#fff', fontSize: 11, fontWeight: 800, padding: '3px 8px', flexShrink: 0 }}>시뮬</span>}
                    </div>
                </div>
            ))}
            {rows.length > FIRST && (
                <button type="button" onClick={() => setAll((v) => !v)} style={{ border: 'none', background: 'none', color: '#667eea', fontSize: 12, fontWeight: 800, cursor: 'pointer', padding: '4px 0 0' }}>
                    {all ? '접기' : `${rows.length - FIRST}개 더 보기`}
                </button>
            )}
        </div>
    );
}
